/**
 * Where the Spotted card blob settings actually live.
 *
 * **Why they need a home of their own.** Leo appearance has a Spotted card
 * tab with size, opacity and X/Y for three blobs, and moving any of them
 * did nothing anywhere in the app. The panel wrote them through the
 * package's `patchPrefs`, and the package persists only the keys in its own
 * prefs schema — `spotted*` is not among them, so every write was dropped
 * on save and `spottedCardBlobFromPrefs` fell back to the defaults on the
 * next read. The controls have never been connected; they were a panel
 * drawn over a value nothing kept.
 *
 * These prefs belong to this app, not to the package, so this app stores
 * them. Same shape and same clamps as `spottedCardBlobFromPrefs`, so if the
 * package ever adopts them the migration is deleting this file.
 *
 * Changes publish synchronously to every subscriber in this tab, and the
 * `storage` event carries them between tabs, so dragging a slider moves the
 * wash on the card behind the panel while the pointer is still down.
 */

import { useCallback, useSyncExternalStore } from "react"

import {
  LEO_SPOTTED_CARD_BLOB_DEFAULTS,
  spottedCardBlobFromPrefs,
  type SpottedCardBlobPrefs,
} from "@/lib/leo-ambience"

const STORAGE_KEY = "exxat.leo.spotted-card-blobs.v1"

let cache: SpottedCardBlobPrefs | null = null
const listeners = new Set<() => void>()

function read(): SpottedCardBlobPrefs {
  if (cache) return cache
  if (typeof window === "undefined") return LEO_SPOTTED_CARD_BLOB_DEFAULTS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    // `spottedCardBlobFromPrefs` fills gaps and clamps, so a partial or
    // stale payload cannot put an out-of-range size on screen.
    cache = spottedCardBlobFromPrefs(raw ? JSON.parse(raw) : null)
  } catch {
    cache = LEO_SPOTTED_CARD_BLOB_DEFAULTS
  }
  return cache
}

function emit() {
  for (const listener of listeners) listener()
}

export function patchSpottedCardBlobPrefs(patch: Partial<SpottedCardBlobPrefs>) {
  const next = spottedCardBlobFromPrefs({ ...read(), ...patch })
  cache = next
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Private mode. The value still applies for this session.
    }
  }
  emit()
}

export function resetSpottedCardBlobPrefs() {
  cache = LEO_SPOTTED_CARD_BLOB_DEFAULTS
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // As above.
    }
  }
  emit()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return
    cache = null
    emit()
  }
  window.addEventListener("storage", onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", onStorage)
  }
}

/** The live Spotted card blob settings. */
export function useSpottedCardBlobPrefs(): SpottedCardBlobPrefs {
  return useSyncExternalStore(subscribe, read, () => LEO_SPOTTED_CARD_BLOB_DEFAULTS)
}

/** Read plus patch, for the Appearance panel. */
export function useSpottedCardBlobControls(): [
  SpottedCardBlobPrefs,
  (patch: Partial<SpottedCardBlobPrefs>) => void,
  () => void,
] {
  const prefs = useSpottedCardBlobPrefs()
  const patch = useCallback(
    (next: Partial<SpottedCardBlobPrefs>) => patchSpottedCardBlobPrefs(next),
    [],
  )
  const reset = useCallback(() => resetSpottedCardBlobPrefs(), [])
  return [prefs, patch, reset]
}
